import {
  HYPHEN_ONLY_PATTERN,
  groupThousands,
  resolveDecimalDigits,
  roundHalfUp,
  shiftPointLeft,
  type DecimalParts,
} from "./decimal";
import { parseDateValue } from "./date";
import {
  formatDurationPartsForDisplay,
  parseStoredDurationValue,
} from "./duration";
import {
  isNumericKind,
  viewValue,
  type IXDStoredValue,
  type IXDViewValue,
  type NumericKind,
  type ValueContext,
} from "./types";

type NumericValueContext = ValueContext & { kind: NumericKind };

/**
 * 저장 숫자 텍스트 파싱 — 콤마 제거, 부호는 괄호 "(…)" 또는 선행 "-"/"+".
 * "(-123)"·"(+123)" 이중 부호는 해석이 갈리므로 null(원문 유지). 그 외 비숫자도 null.
 *
 * 선행 "+"를 받는 이유: canonical 저장값에는 없지만 IXD 원문(column.text)에는 올 수
 * 있다. 여기서 null이 나면 storedToView가 원문을 그대로 돌려줘 콤마와 negated 반전이
 * 함께 조용히 빠진다 — MONETARY는 Number 폴백이 있어 반전만은 살지만 NUMERIC·PERCENT는
 * 폴백이 없다. 입력 방향(decimal.ts parseStrictDecimal)과 같이 "+"는 버리고 양수로 본다.
 */
function parseStoredNumberText(trimmed: string): DecimalParts | null {
  let negative = false;
  let body = trimmed;
  if (body.startsWith("(") && body.endsWith(")")) {
    negative = true;
    body = body.slice(1, -1).trim();
    if (/^[+-]/.test(body)) return null;
  } else if (body.startsWith("-")) {
    negative = true;
    body = body.slice(1);
  } else if (body.startsWith("+")) {
    body = body.slice(1);
  }
  body = body.replace(/,/g, "");
  if (!/^\d+(\.\d+)?$/.test(body)) return null;

  const dotIdx = body.indexOf(".");
  return {
    negative,
    int: dotIdx >= 0 ? body.slice(0, dotIdx) : body,
    frac: dotIdx >= 0 ? body.slice(dotIdx + 1) : "",
  };
}

/**
 * 이 값을 화면에 음수(괄호)로 보여줘야 하는가.
 * 표시 부호 = 저장 부호 XOR negated 서식. 값이 0이면 부호가 없으므로 항상 false.
 * 저장 방향(toStored.flipSignIfNegated)과 대칭인 규칙의 유일한 집.
 */
const shouldDisplayAsNegative = ({
  isStoredNegative,
  isZero,
  negated,
}: {
  /** 저장값 자체의 부호가 음수인가 ("-123", "(123)") */
  isStoredNegative: boolean;
  isZero: boolean;
  /** 라인아이템의 negated 서식 여부 */
  negated: boolean;
}): boolean => !isZero && isStoredNegative !== negated;

/**
 * 숫자 kind(NUMERIC_KINDS) 공통 표시 파이프라인 — 문자열 십진 연산 (Number 미경유,
 * 대형 정수 무손실). kind별 차이는 대괄호로 표시한 단계뿐이다.
 *
 * 파싱 → [MONETARY] 10^scale 소수점 좌이동 → dd half-up 반올림(+trailing zero 패딩;
 * dd 미지정+정수 원본→0자리, 미지정+소수→그대로) → [MONETARY·NUMERIC] 정수부 천 단위
 * 콤마 → negated XOR 부호로 음수 괄호 "(…)"(값 0이면 괄호 없음).
 *
 * dd 반올림·패딩은 숫자 kind 전체에 적용한다. 저장 canonical은 trailing zero를 갖지
 * 못하므로(types.ts ensureCanonical) "0.04"를 dd=4 항목에서 "0.0400"으로 보이게 하려면
 * 표시 단계가 유일한 패딩 지점이다 — MONETARY만 패딩하면 PERCENT·NUMERIC 셀의 자릿수가
 * 같은 행 헤더 dd를 두고도 셀마다 달라진다.
 * 하이픈 단독은 재무제표의 "값 없음" 표기라 0으로 읽고 같은 파이프라인을 태운다 —
 * dd=2인 행에서 값 셀은 "0.00", 하이픈 셀만 "0"이면 자릿수가 갈리므로 early return하지
 * 않는다(dd 미지정이면 "0"). 파싱 불가·scale 비정상이면 null(호출부가 원문 반환).
 *
 * storedToView는 실패 개념이 없어 null을 삼키지만, 편집기 표시 경로는 "문자열 십진이
 * 못 다루면 Number 경로로 폴백"이라는 별도 계약이 있어 null을 직접 봐야 한다
 * (ixd-editor/unit-conversion.ts:formatMonetaryTextForDisplay).
 */
export function formatNumericStoredForDisplay(
  stored: string,
  ctx: NumericValueContext,
): string | null {
  const trimmed = stored.trim();
  if (!trimmed) return null;

  const parsed: DecimalParts | null = HYPHEN_ONLY_PATTERN.test(trimmed)
    ? { negative: false, int: "0", frac: "" }
    : parseStoredNumberText(trimmed);
  if (!parsed) return null;

  let intPart = parsed.int;
  let fracPart = parsed.frac;
  if (ctx.kind === "MONETARY") {
    const scale = ctx.scale ?? 0;
    if (!Number.isInteger(scale) || scale < 0) return null;
    ({ int: intPart, frac: fracPart } = shiftPointLeft(parsed, scale));
  }

  // dd 미지정이면 원본 자릿수를 유지한다. 정수 원본은 0자리 반올림으로 단위 환산
  // 소수를 정리하고(MONETARY), 소수 원본은 그대로 둔다(비MONETARY는 시프트가 없어 no-op).
  const roundTo =
    resolveDecimalDigits(ctx.decimalDigits) ?? (parsed.frac === "" ? 0 : null);
  if (roundTo !== null) {
    [intPart, fracPart] = roundHalfUp(intPart, fracPart, roundTo);
  }

  intPart = intPart.replace(/^0+(?=\d)/, "") || "0";
  const isZero = /^0+$/.test(intPart) && /^0*$/.test(fracPart);
  const groupedInt = ctx.kind === "PERCENT" ? intPart : groupThousands(intPart);
  const body = fracPart ? `${groupedInt}.${fracPart}` : groupedInt;
  return shouldDisplayAsNegative({
    isStoredNegative: parsed.negative,
    isZero,
    negated: Boolean(ctx.negated),
  })
    ? `(${body})`
    : body;
}

/**
 * 저장 canonical → 표시값 (value-spec 06). 실패 개념 없음 — 저장값은 이미
 * 검증됐으므로 파싱 불가 시 원문을 그대로 반환한다(예외 금지).
 *
 * 숫자 kind: formatNumericStoredForDisplay 한 경로("-"→"0", ""→"").
 * DATE: dashed. DURATION: 한국어(P0D→"0일"). BOOLEAN·TEXT: 원문.
 */
export function storedToView(
  stored: IXDStoredValue,
  ctx: ValueContext,
): IXDViewValue {
  return viewValue(storedToViewText(stored.text, ctx));
}

// VO는 경계에서만 감싼다 — 내부 서식화는 문자열 기반으로 유지한다.
function storedToViewText(stored: string, ctx: ValueContext): string {
  if (isNumericKind(ctx.kind)) {
    return (
      formatNumericStoredForDisplay(stored, { ...ctx, kind: ctx.kind }) ??
      stored
    );
  }

  switch (ctx.kind) {
    case "DATE": {
      const parsed = parseDateValue(stored);
      return parsed ? parsed.dashedValue : stored;
    }

    case "DURATION": {
      const parsed = parseStoredDurationValue(stored);
      return parsed ? formatDurationPartsForDisplay(parsed) : stored;
    }

    default:
      return stored;
  }
}
